import { injectable } from 'inversify';
import * as signalR from '@microsoft/signalr';
import { LogService } from 'services/LogService';
import { useCypressSignalRMock } from 'cypress-signalr-mock';
import { AuthenticationService } from '../AuthenticationService';

@injectable()
export class BallFinalFeedService {
  private onBallFinalReceived?: (ballFinal: string) => void;
  private onSpin3DReceived?: (spin3dFinal: string) => void;
  private onPlayDataReceived?: (playData: string) => void;
  private onStrikeZoneDecisionReceived?: (playData: string) => void;

  private connection: signalR.HubConnection;
  private logService: LogService;
  private groupName?: string;
  private networkSharingRules?: boolean;
  private isConnected: boolean = false;
  private authenticationService: AuthenticationService;

  constructor(logService: LogService, authenticationService: AuthenticationService) {
    this.logService = logService;
    this.authenticationService = authenticationService;

    this.connection =
      (useCypressSignalRMock('cypressMockHub') as any) ??
      new signalR.HubConnectionBuilder()
        // .withUrl('https://localhost:5001/broadcast', {
        .withUrl('/broadcast', {
          accessTokenFactory: () => this.authenticationService.getAuthData().accessToken,
        })
        .configureLogging(signalR.LogLevel.Information)
        .build();
    // set timeout to 10 minutes
    this.connection.serverTimeoutInMilliseconds = 1000 * 60 * 10;

    this.connection.onclose(async (error: any) => {
      console.log(`Connection closed`);
      if (error) {
        console.log(`because of error: ${error}`);
        console.log(error);
        this.logService.logError({ error: error, message: `Connection closed with exception.` });
      }
      if (this.groupName && this.onBallFinalReceived) {
        console.log(`retrying...`);
        await this.start();
      }
    });
  }

  watch = async (
    groupName: string,
    networkSharingRules: boolean,
    onBallFinalReceived: (ballFinal: any) => void,
    onSpin3DReceived: (spin3dfinal: any) => void,
    onPlayDataReceived: (playData: any) => void,
    onStrikeZoneDecisionReceived: (strikeZoneDecision: any) => void
  ) => {
    if (this.groupName) {
      await this.stop();
    }
    this.groupName = groupName;
    this.networkSharingRules = networkSharingRules;
    this.onBallFinalReceived = onBallFinalReceived;
    this.onSpin3DReceived = onSpin3DReceived;
    this.onPlayDataReceived = onPlayDataReceived;
    this.onStrikeZoneDecisionReceived = onStrikeZoneDecisionReceived;
    await this.start();
  };

  stop = async () => {
    if (this.isConnected) {
      this.isConnected = false;
      console.log('Disconnecting...');
      await this.unsubscribeBallEvents().then(async () => {
        this.groupName = undefined;
        this.networkSharingRules = false;
        this.onBallFinalReceived = undefined;
        this.onPlayDataReceived = undefined;
        this.onStrikeZoneDecisionReceived = undefined;
        if (
          this.connection.state != signalR.HubConnectionState.Disconnected &&
          this.connection.state != signalR.HubConnectionState.Disconnecting
        ) {
          await this.connection.stop();
        }
        console.log('Disconnected');
      });
    }
  };

  private start = async () => {
    console.log('Connecting...');
    try {
      await this.connection
        .start()
        .then((_) => {
          this.joinAndHandle()
            .then((_) => {
              this.isConnected = true;
              console.log('Connected');
            })
            .catch((err) => {
              console.log('JoinAndHandle Promise exception...');
              console.log(err);
            });
        })
        .catch((err) => {
          console.log('Start Promise exception...');
          console.log(err);
        });
    } catch (err) {
      this.logService.logError({ error: err, message: `Exception when connecting to data. Retrying ...` });
      console.log(err);
      setTimeout(() => this.start(), 5000);
    }
  };

  private joinAndHandle = (): Promise<any> => {
    return this.connection.invoke('JoinGroup', this.groupName, this.networkSharingRules).then((res) => {
      if (this.onBallFinalReceived != undefined) {
        this.connection.on('SendBallFinal', this.onBallFinalReceived);
      }
      if (this.onSpin3DReceived != undefined) {
        this.connection.on('SendSpin3D', this.onSpin3DReceived);
      }
      if (this.onPlayDataReceived != undefined) {
        this.connection.on('SendPlayData', this.onPlayDataReceived);
      }
      if (this.onStrikeZoneDecisionReceived != undefined) {
        this.connection.on('SendStrikeZoneDecision', this.onStrikeZoneDecisionReceived);
      }
    });
  };

  private unsubscribeBallEvents = (): Promise<any> => {
    if (this.groupName != undefined) {
      return this.connection.invoke('RemoveFromGroup', this.groupName).then((_) => {
        this.connection.off('SendBallFinal');
        this.connection.off('SendSpin3D');
        this.connection.off('SendPlayData');
        this.connection.off('SendStrikeZoneDecision');
      });
    }
    return new Promise(() => {
      this.connection.off('SendBallFinal');
      this.connection.off('SendSpin3D');
      this.connection.off('SendPlayData');
      this.connection.off('SendStrikeZoneDecision');
    });
  };
}
